import { useState } from 'react';
import { Button, Flex, Modal, Typography, message } from 'antd';

import {
	PROCESS_LABELS,
	type ProcessKind,
	type ProcessSpecRef,
} from '../../interfaces/ProcessCatalog.Interface';
import { useApplyOrderProcessesMutation } from '../../redux/api/order';
import { useGetProcessSpecsQuery } from '../../redux/api/processCatalog';
import { extractApiError } from '../../utils/apiError';
import ProcessSpecSelect from './ProcessSpecSelect';

const { Text } = Typography;

interface Props {
	orderId: string;
	lineItemCount: number;
	handlingSpec: ProcessSpecRef | null | undefined;
	cleaningSpec: ProcessSpecRef | null | undefined;
	disabled?: boolean;
	refetch: () => void;
}

/**
 * Handling and cleaning for the whole order.
 *
 * Almost always the right granularity — one customer, one way of handling their
 * parts — so this is where it is set, and the per-line-item pickers exist for
 * the exception. Applying is deliberately a button and not an on-change save:
 * it rewrites every line item, including ones somebody set by hand, so it asks
 * first.
 */
export default function OrderProcessesApply({
	orderId,
	lineItemCount,
	handlingSpec,
	cleaningSpec,
	disabled = false,
	refetch,
}: Props) {
	const [applyProcesses, { isLoading }] = useApplyOrderProcessesMutation();
	const [messageApi, messageContext] = message.useMessage();
	const [pending, setPending] = useState<{
		kind: 'handling' | 'cleaning';
		specId: string;
	} | null>(null);

	// Only to name the row in the confirmation; the pickers load their own.
	const { data: specs = [] } = useGetProcessSpecsQuery(
		pending ? { kind: pending.kind, is_active: true } : undefined,
		{ skip: !pending }
	);
	const pendingName =
		specs.find((spec) => spec.id === pending?.specId)?.name ?? '';

	async function apply() {
		if (!pending) return;
		const field = pending.kind === 'handling' ? 'handling_spec' : 'cleaning_spec';
		try {
			await applyProcesses({ id: orderId, [field]: pending.specId }).unwrap();
			setPending(null);
			refetch();
			messageApi.open({
				type: 'success',
				content: `${PROCESS_LABELS[pending.kind]} applied to every line item.`,
			});
		} catch (error) {
			messageApi.open({ type: 'error', content: extractApiError(error) });
		}
	}

	return (
		<>
			{messageContext}

			<Flex vertical align="start" gap={4} className="min-w-0">
				<span className="text-slate-400">Handling (all line items)</span>
				<ProcessSpecSelect
					kind={'handling' as ProcessKind}
					value={handlingSpec}
					onChange={(specId) =>
						specId && setPending({ kind: 'handling', specId })
					}
					disabled={disabled || isLoading}
				/>
			</Flex>

			<Flex vertical align="start" gap={4} className="min-w-0">
				<span className="text-slate-400">Cleaning (all line items)</span>
				<ProcessSpecSelect
					kind={'cleaning' as ProcessKind}
					value={cleaningSpec}
					onChange={(specId) =>
						specId && setPending({ kind: 'cleaning', specId })
					}
					disabled={disabled || isLoading}
				/>
			</Flex>

			<Modal
				open={pending !== null}
				title={`Apply ${
					pending ? PROCESS_LABELS[pending.kind] : ''
				} to every line item?`}
				onCancel={() => setPending(null)}
				footer={[
					<Button key="cancel" onClick={() => setPending(null)}>
						Cancel
					</Button>,
					<Button
						key="apply"
						type="primary"
						loading={isLoading}
						onClick={apply}
					>
						Apply to all {lineItemCount} line item
						{lineItemCount === 1 ? '' : 's'}
					</Button>,
				]}
			>
				<Text>
					Every line item on this order will be set to{' '}
					<Text strong>{pendingName}</Text>, including any that were changed
					on their own detail page.
				</Text>
			</Modal>
		</>
	);
}
